import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getAuctionCalendarCountryFacets,
    getAuctionCalendarHouseFacets,
    getAuctionCalendarIds,
    getAuctionCalendarLiveBroadcastFacets,
    getAuctionCalendarSaleTypeFacets,
    getAuctionCalendarShippingFacets,
    getAuctionCalendarSubmitted,
    getAuctionCalendarTopRated,
    getAuctionCalendarTotalCount,
    getCatalogsTodayCount,
} from '@/redux/modules/auctionCalendar/auctionCalendar.selectors';
import { getBaseUrl } from '@/redux/modules/config';
import { changePaginationPageSize, getSearchPageSize } from '@/redux/modules/pagination';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import {
    modifyParamsRemovePage,
    modifyParamsWhenChecked,
    modifyParamsWithDate,
    modifyParamsWithPage,
    modifyParamsWithPageSize,
    parseQueryParams,
} from '@/utils/queryParams';
import { getAuctionCalendarUrlWithParams } from '@/utils/urls';

import { AuctionCalendarPage } from './AuctionCalendarPage';

const AuctionCalendarPageContainer = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const queryParams: any = React.useMemo(() => parseQueryParams(location.search), [location.search]);
    const { pathname } = location;

    const catalogIds = useAppSelector(getAuctionCalendarIds);
    const totalCatalogsFound = useAppSelector(getAuctionCalendarTotalCount);
    const catalogsTodayCount = useAppSelector(getCatalogsTodayCount);
    const baseUrl = useAppSelector(getBaseUrl);

    const auctionHouses = useAppSelector(getAuctionCalendarHouseFacets);
    const countries = useAppSelector(getAuctionCalendarCountryFacets);
    const liveBroadcastOptions = useAppSelector(getAuctionCalendarLiveBroadcastFacets);
    const shippingOptions = useAppSelector(getAuctionCalendarShippingFacets);
    const saleTypes = useAppSelector(getAuctionCalendarSaleTypeFacets);
    const topRatedAuctionHouses = useAppSelector(getAuctionCalendarTopRated);

    const selectedDate = queryParams.date || '';
    const page = queryParams.page || 1;
    const reduxPageSize = useAppSelector(getSearchPageSize);
    const pageSize = queryParams.pageSize || reduxPageSize;

    const searchSubmitted = useAppSelector(getAuctionCalendarSubmitted);
    const whiteLabel = useAppSelector(getIsWhiteLabel);

    const navigateWithParams = React.useCallback(
        (params: { [key: string]: string | number | boolean }) => {
            let newParams = params;
            if (params.page === 1) {
                newParams = modifyParamsRemovePage(newParams);
            }
            navigate(getAuctionCalendarUrlWithParams(newParams));
        },
        [navigate]
    );

    const changePageAndNavigate = React.useCallback(
        (selected: number) => {
            const page = selected + 1;
            const newParams = modifyParamsWithPage(queryParams, page);
            navigateWithParams(newParams);
        },
        [navigateWithParams, queryParams]
    );

    const changePageSizeAndNavigate = React.useCallback(
        (pageSize: number) => {
            dispatch(changePaginationPageSize(pageSize));
            const newParams = modifyParamsWithPageSize(queryParams, pageSize);
            navigateWithParams(newParams);
        },
        [dispatch, navigateWithParams, queryParams]
    );

    const facetOptionChecked = React.useCallback(
        (facetId: string, value: any, checked: boolean) => {
            let newParams = modifyParamsWhenChecked(queryParams, facetId, value, checked);
            newParams = modifyParamsRemovePage(newParams);
            navigateWithParams(newParams);
        },
        [navigateWithParams, queryParams]
    );

    const dateChanged = React.useCallback(
        (date: number | Date) => {
            let newParams = modifyParamsWithDate(queryParams, date);
            newParams = modifyParamsRemovePage(newParams);
            navigateWithParams(newParams);
        },
        [navigateWithParams, queryParams]
    );

    return (
        <AuctionCalendarPage
            auctionHouses={auctionHouses}
            baseUrl={baseUrl}
            catalogIds={catalogIds}
            catalogsTodayCount={catalogsTodayCount}
            changePageAndNavigate={changePageAndNavigate}
            changePageSizeAndNavigate={changePageSizeAndNavigate}
            countries={countries}
            dateChanged={dateChanged}
            facetOptionChecked={facetOptionChecked}
            liveBroadcastOptions={liveBroadcastOptions}
            page={page}
            pageSize={pageSize}
            pathname={pathname}
            queryParams={queryParams}
            saleTypes={saleTypes}
            searchSubmitted={searchSubmitted}
            selectedDate={selectedDate}
            shippingOptions={shippingOptions}
            topRatedAuctionHouses={topRatedAuctionHouses}
            totalCatalogsFound={totalCatalogsFound}
            whiteLabel={whiteLabel}
        />
    );
};

export default AuctionCalendarPageContainer;
